import * as signalR from '@microsoft/signalr';
import type { SignalRMessage } from 'types';

// SignalR hub URL - SignalR automatically appends /negotiate
const SIGNALR_HUB_URL = '/api/signalr';
const MAX_CONNECTION_RETRIES = 5;
// SignalR is now enabled with backend negotiate endpoint
const SIGNALR_ENABLED = true;

export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting' | 'failed';

export type SignalRMessageHandler = (message: SignalRMessage) => void;
export type ConnectionStatusHandler = (status: ConnectionStatus) => void;

// ─── Token Provider ───
type TokenProvider = () => Promise<string | null>;
let signalRTokenProvider: TokenProvider | null = null;

/**
 * Set the token provider for SignalR authentication.
 * Called once during app initialization with the auth context's getAccessToken.
 */
export function setSignalRTokenProvider(provider: TokenProvider): void {
  signalRTokenProvider = provider;
}

let connection: signalR.HubConnection | null = null;
let connectionRetryCount = 0;
let connectionFailed = false;
const messageHandlers = new Set<SignalRMessageHandler>();
const statusHandlers = new Set<ConnectionStatusHandler>();

function notifyStatus(status: ConnectionStatus): void {
  statusHandlers.forEach((handler) => handler(status));
}

/**
 * Custom connection factory for Azure SignalR Service serverless mode.
 * Fetches connection info from the negotiate endpoint and creates a connection
 * that skips the secondary negotiate (required for Azure SignalR Service).
 */
async function createAzureSignalRConnection(): Promise<signalR.HubConnection> {
  // Build headers for negotiate request
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  
  // Attach bearer token if provider is set
  if (signalRTokenProvider) {
    const token = await signalRTokenProvider();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }

  // Fetch connection info from our negotiate endpoint
  const negotiateResponse = await fetch(`${SIGNALR_HUB_URL}/negotiate`, {
    method: 'POST',
    headers,
  });

  if (!negotiateResponse.ok) {
    throw new Error(`Negotiate failed: ${negotiateResponse.status} ${negotiateResponse.statusText}`);
  }

  const connectionInfo = await negotiateResponse.json();

  if (!connectionInfo.url || !connectionInfo.accessToken) {
    throw new Error('Invalid negotiate response: missing url or accessToken');
  }

  // Create connection with Azure SignalR Service URL
  // Use skipNegotiation because we already have the connection info
  const conn = new signalR.HubConnectionBuilder()
    .withUrl(connectionInfo.url, {
      accessTokenFactory: () => connectionInfo.accessToken,
      skipNegotiation: true,
      transport: signalR.HttpTransportType.WebSockets
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) => {
        if (retryContext.previousRetryCount >= MAX_CONNECTION_RETRIES) {
          return null;
        }
        return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 16000);
      }
    })
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  return conn;
}

export function getConnection(): signalR.HubConnection | null {
  return connection;
}

/**
 * Normalizes property names from PascalCase to camelCase.
 * Azure SignalR output binding serializes with PascalCase by default,
 * but frontend expects camelCase.
 */
function normalizeMessage(raw: Record<string, unknown>): SignalRMessage {
  const incident = raw.Incident || raw.incident;
  const normalizedIncident = incident ? normalizeIncident(incident as Record<string, unknown>) : undefined;
  
  return {
    eventType: (raw.EventType || raw.eventType) as string,
    incidentId: (raw.IncidentId || raw.incidentId) as string,
    severity: (raw.Severity ?? raw.severity) as number,
    state: (raw.State ?? raw.state) as number,
    customerId: (raw.CustomerId || raw.customerId) as string,
    incident: normalizedIncident,
  } as SignalRMessage;
}

function normalizeIncident(raw: Record<string, unknown>): SignalRMessage['incident'] {
  const tagsRaw = (raw.Tags || raw.tags) as Record<string, unknown> | undefined;
  const tags = tagsRaw
    ? {
        isCritical: Boolean(tagsRaw.IsCritical ?? tagsRaw.isCritical),
        nis2Categories: ((tagsRaw.Nis2Categories ?? tagsRaw.nis2Categories) as string[] | undefined) ?? [],
      }
    : { isCritical: false, nis2Categories: [] };

  return {
    incidentId: (raw.IncidentId || raw.incidentId) as string,
    incidentArmId: (raw.IncidentArmId || raw.incidentArmId) as string | null,
    customerId: (raw.CustomerId || raw.customerId) as string,
    severity: (raw.Severity ?? raw.severity) as number,
    state: (raw.State ?? raw.state) as number,
    title: (raw.Title || raw.title) as string,
    assignedOperatorId: (raw.AssignedOperatorId || raw.assignedOperatorId) as string | null,
    assignedOperatorName: (raw.AssignedOperatorName || raw.assignedOperatorName) as string | null,
    acknowledgedTime: (raw.AcknowledgedTime || raw.acknowledgedTime) as string | null,
    ownedTime: (raw.OwnedTime || raw.ownedTime) as string | null,
    createdTime: (raw.CreatedTime || raw.createdTime) as string,
    lastUpdateTime: (raw.LastUpdateTime || raw.lastUpdateTime) as string,
    slaDeadline: (raw.SlaDeadline || raw.slaDeadline) as string,
    escalationLevel: (raw.EscalationLevel ?? raw.escalationLevel) as number,
    sentinelUrl: (raw.SentinelUrl || raw.sentinelUrl) as string | null,
    slaType: (raw.SlaType ?? raw.slaType ?? 0) as number,
    tags,
  };
}

async function setupConnection(): Promise<signalR.HubConnection> {
  if (connection) {
    return connection;
  }

  connection = await createAzureSignalRConnection();

  connection.on('incidentUpdate', (rawMessage: Record<string, unknown>) => {
    const message = normalizeMessage(rawMessage);
    messageHandlers.forEach((handler) => handler(message));
  });

  connection.onreconnecting(() => {
    notifyStatus('reconnecting');
  });
  connection.onreconnected(() => {
    connectionRetryCount = 0;
    connectionFailed = false;
    notifyStatus('connected');
  });
  connection.onclose(() => {
    if (connectionRetryCount >= MAX_CONNECTION_RETRIES) {
      connectionFailed = true;
      notifyStatus('failed');
    } else {
      notifyStatus('disconnected');
    }
  });

  return connection;
}

export async function startConnection(): Promise<void> {
  // SignalR disabled - don't attempt connection
  if (!SIGNALR_ENABLED) {
    notifyStatus('disconnected');
    return;
  }
  
  // If we've exceeded max retries, don't attempt again
  if (connectionFailed) {
    return;
  }

  // If already connected or connecting, don't start again
  if (connection && (
    connection.state === signalR.HubConnectionState.Connected ||
    connection.state === signalR.HubConnectionState.Connecting ||
    connection.state === signalR.HubConnectionState.Reconnecting
  )) {
    return;
  }

  try {
    const conn = await setupConnection();

    if (conn.state === signalR.HubConnectionState.Disconnected) {
      await conn.start();
      connectionRetryCount = 0;
      connectionFailed = false;
      notifyStatus('connected');
    }
  } catch (err) {
    connectionRetryCount++;
    console.warn(`SignalR connection attempt ${connectionRetryCount}/${MAX_CONNECTION_RETRIES} failed:`, err);
    
    // Reset connection so we can try fresh negotiate
    connection = null;
    
    if (connectionRetryCount >= MAX_CONNECTION_RETRIES) {
      connectionFailed = true;
      notifyStatus('failed');
      console.error('SignalR connection failed after max retries. Reload page to retry.');
    } else {
      notifyStatus('disconnected');
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      const delay = Math.min(1000 * Math.pow(2, connectionRetryCount - 1), 16000);
      setTimeout(() => startConnection(), delay);
    }
  }
}

/** Reset retry state and attempt connection again */
export function retryConnection(): void {
  connectionRetryCount = 0;
  connectionFailed = false;
  connection = null;
  startConnection();
}

export async function stopConnection(): Promise<void> {
  if (connection) {
    await connection.stop();
    connection = null;
    notifyStatus('disconnected');
  }
}

export function onMessage(handler: SignalRMessageHandler): () => void {
  messageHandlers.add(handler);
  return () => {
    messageHandlers.delete(handler);
  };
}

export function onConnectionStatus(handler: ConnectionStatusHandler): () => void {
  statusHandlers.add(handler);
  return () => {
    statusHandlers.delete(handler);
  };
}
